import * as React from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  Row,
  RowModel,
  RowSelectionState,
} from '@tanstack/react-table';
import type { Pagination } from 'contracts/Pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import { cn } from 'lib/utils';

import { Input } from '../input';
import { DataTablePagination } from '../DataTable/data-table-pagination';
import { DataTableViewOptions } from '../DataTable/data-table-view-options';
import { Skeleton } from '../DataTable/data-table-skeleton';
import { getPinningStyles, pinColumns } from '../DataTable/utils';
import { Button } from '../ButtonGs';

export type SortingProps = {
  orderBy: string;
  orderByDirection: 'asc' | 'desc';
}

export type DataTableV2SelectionChangeProps<T> = {
  selectedRows: T[];
  selectedRowIds: string[];
  totalRows: number;
};

interface DataTableProps<TData, TValue = unknown> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  initialColumnVisibility?: VisibilityState;
  filterBy?: string;
  isLoading?: boolean;
  showControlColumns?: boolean;
  inlineTopBarControls?: boolean;
  pageSize?: number;
  selectable?: boolean;
  selectionAction?: (data: RowModel<TData>) => void;
  selectionActionLabel?: string;
  filterLabel?: string;
  rowColor?: (row: TData) => string;
  onTableInstance?: (tableInstance: any) => void;
  pagination?: Partial<Pagination>;
  manualPagination?: boolean;
  onPaginationChange?: (pagination: { page: number; limit: number }) => void;
  onSort?: (prop: SortingProps) => void;
  noBreakHeader?: boolean;
  showPagination?: boolean;
  onRowClick?: (row: Row<TData>) => void;
  stickyHeader?: boolean;
  tableWrapperClassName?: string;
  tableWrapperStyle?: React.CSSProperties;
  /**
   * For better results, set the size for each column
   */
  leftPinnedColumns?: string[];
  /**
   * For better results, set the size for each column
   */
  rightPinnedColumns?: string[];
  renderTopBar?: (props: DataTableV2SelectionChangeProps<TData>) => React.ReactNode;
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
  onSelectionChange?: (props: DataTableV2SelectionChangeProps<TData>) => void;
}

export type DataTableV2Ref = {
  clearSelection: () => void;
}

export const DataTableV2 = React.forwardRef<DataTableV2Ref, DataTableProps<any>>(
  <TData, TValue>({
                    columns,
                    data,
                    initialColumnVisibility = {},
                    filterBy,
                    filterLabel,
                    isLoading,
                    showControlColumns,
                    inlineTopBarControls = false,
                    pageSize = 10,
                    selectable,
                    selectionAction,
                    selectionActionLabel,
                    noBreakHeader,
                    rowColor,
                    onTableInstance,
                    manualPagination,
                    showPagination = true,
                    pagination = {
                      currentPage: 1,
                      lastPage: 10,
                    },
                    onPaginationChange,
                    onSort,
                    onRowClick,
                    stickyHeader,
                    renderTopBar,
                    tableWrapperClassName,
                    tableWrapperStyle,
                    leftPinnedColumns,
                    rightPinnedColumns,
                    enableRowSelection,
                    onSelectionChange,
                    ...tableProps
                  }: DataTableProps<TData, TValue>,
                  ref: React.Ref<DataTableV2Ref>,
  ) => {
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
      [],
    );
    const [columnVisibility, setColumnVisibility] =
      React.useState<VisibilityState>(initialColumnVisibility);
    const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
    const [globalRowSelection, setGlobalRowSelection] = React.useState<TData[]>([]);

    const paginationFilter = React.useMemo(() => ({
        pageIndex: pagination?.currentPage ? pagination.currentPage - 1 : 0,
        pageSize: pagination.perPage || 10,
      }),
      [
        pagination?.currentPage,
        pagination?.perPage,
      ],
    );

    const setPaginationFilter = React.useCallback(
      (updater: any) => {
        let nextPagination =
          typeof updater === 'function' ? updater(paginationFilter) : updater;

        if (onPaginationChange) {
          onPaginationChange({
            page: nextPagination.pageIndex + 1,
            limit: nextPagination.pageSize,
          });
        }
      },
      [onPaginationChange, paginationFilter],
    );

    const onSortingChange = React.useCallback(() => {
      const orderBy = sorting?.[0]?.id;
      const orderByDirection = sorting?.[0]?.desc ? 'desc' : 'asc';

      if (!onSort || !orderBy) return;

      onSort({ orderBy, orderByDirection });
    }, [onSort, sorting]);

    const hasPinnedColumns = React.useMemo(() => {
      return leftPinnedColumns?.length || rightPinnedColumns?.length;
    }, [leftPinnedColumns?.length, rightPinnedColumns?.length]);

    const table = useReactTable({
      data,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      onColumnFiltersChange: setColumnFilters,
      onColumnVisibilityChange: setColumnVisibility,
      onSortingChange: setSorting,
      onRowSelectionChange: setRowSelection,
      manualSorting: Boolean(onSort),
      getRowId: (row: any) => row.id,
      ...(enableRowSelection !== undefined && {
        enableRowSelection,
      }),
      ...(manualPagination
        ? {
          manualPagination: manualPagination,
          onPaginationChange: setPaginationFilter,
          pageCount: pagination?.lastPage,
        }
        : {}),
      state: {
        sorting,
        columnFilters,
        columnVisibility,
        rowSelection,
        ...(manualPagination ? { pagination: paginationFilter } : {}),
      },
      initialState: {
        pagination: manualPagination
          ? paginationFilter
          : {
            pageSize: pageSize,
          },
      },
    });

    const updateGlobalRowSelectionOnSelectionChange = React.useCallback(() => {
      const currentPageSelectableIds = table.getRowModel().rows
        .map((row) => (row.original as unknown as any).id)
        .map((id) => `${id}`);
      const currentPageSelection = table.getSelectedRowModel().rows
        .map((row) => row.original);

      setGlobalRowSelection((state) => {
        const stateCopy = [...state];

        const otherPageRows = stateCopy.filter((row: any) => {
          return !currentPageSelectableIds.includes(`${row.id}`);
        });

        return [...otherPageRows, ...currentPageSelection].sort((a: any, b: any) => {
          return a.id - b.id;
        });
      });
    }, [
      table.getRowModel().rows,
      table.getSelectedRowModel().rows,
    ]);

    const callOnSelectionChange = React.useCallback(() => {
      if (!onSelectionChange) return;
      const totalRows = pagination?.total || table.getRowModel().rows.length;
      const selectedRowIds = globalRowSelection.map((row: any) => row.id);

      onSelectionChange({
        selectedRows: globalRowSelection,
        selectedRowIds,
        totalRows,
      });
    }, [
      onSelectionChange,
      globalRowSelection,
      pagination?.total,
    ]);

    const TopBarComponent = React.useMemo(() => {
      if (!renderTopBar) return null;
      const totalRows = pagination?.total || table.getRowModel().rows.length;
      const selectedRowIds = globalRowSelection.map((row: any) => row.id);

      return renderTopBar({
        selectedRows: globalRowSelection,
        selectedRowIds,
        totalRows,
      });
    }, [
      renderTopBar,
      globalRowSelection,
      pagination?.total,
    ]);

    React.useEffect(() => {
      if (onTableInstance) {
        onTableInstance(table);
      }
    }, [table, onTableInstance]);

    React.useEffect(() => {
      onSortingChange();
    }, [onSortingChange]);

    React.useEffect(() => {
      callOnSelectionChange();
    }, [callOnSelectionChange]);

    React.useEffect(() => {
      updateGlobalRowSelectionOnSelectionChange();
    }, [updateGlobalRowSelectionOnSelectionChange]);

    React.useEffect(() => {
      if (leftPinnedColumns?.length || rightPinnedColumns?.length) {
        pinColumns({
          headerGroups: table.getHeaderGroups(),
          leftPinnedColumns,
          rightPinnedColumns,
        });
      }
    }, [leftPinnedColumns, rightPinnedColumns]);

    React.useImperativeHandle(ref, () => ({
      clearSelection: () => {
        table.setRowSelection({});
        setGlobalRowSelection([]);
      },
    }), []);

    if (isLoading) {
      return <Skeleton />;
    }

    if (!data.length) {
      return (
        <div className="flex items-center justify-center h-24 text-center font-sans">
          Sem resultados
        </div>
      );
    }

    return (
      <div>
        <div className="w-full mb-4 flex flex-col md:flex-row gap-3 items-center justify-between">
          {filterBy && (
            <Input
              placeholder={`Filtrar ${filterBy.toLowerCase()} ...`}
              value={
                (table.getColumn(filterBy)?.getFilterValue() as string) ?? ''
              }
              onChange={(event) =>
                table.getColumn(filterBy)?.setFilterValue(event.target.value)
              }
              className="max-w-sm focus-visible:ring-primary-500"
            />
          )}
          {TopBarComponent}
          {selectionAction && (
            <Button.Root
              onClick={() => selectionAction(table.getFilteredSelectedRowModel())}
              size="sm"
              disabled={!table.getFilteredSelectedRowModel().rows.length}
            >
              {selectionActionLabel ?? 'Action'} (
              {table.getFilteredSelectedRowModel().rows.length}/
              {table.getFilteredRowModel().rows.length} )
            </Button.Root>
          )}
          {showControlColumns && (
            <div
              className={cn(
                inlineTopBarControls
                  ? 'flex gap-3 w-fit justify-end'
                  : 'flex flex-1 gap-3 w-full justify-end'
              )}
            >
              <DataTableViewOptions table={table} />
            </div>
          )}
        </div>
        <div className="rounded-md border bg-white">
          <Table {...tableProps}
                 wrapperClassName={cn(
                   'flex-1 overflow-y-auto',
                   noBreakHeader && '[&>thead>tr>th>div]:whitespace-nowrap',
                   tableWrapperClassName,
                 )}
                 wrapperStyle={tableWrapperStyle}
                 className={'relative bg-background'}
                 style={{
                   ...(hasPinnedColumns && {
                     /*
                       Adding some pixels to the total size to avoid a visual bug
                       described on the issue https://github.com/TanStack/table/issues/5783
                      */
                     minWidth: table.getTotalSize() + 40,
                     width: '100%',
                   }),
                 }}
          >
            <TableHeader className={cn(stickyHeader && 'sticky top-0 z-10 bg-inherit')}>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const pinningStyles = getPinningStyles(header.column);
                    const stickyHeaderStyles = stickyHeader
                      ? {
                          // Mantem o cabecalho fixo; os estilos de pinagem podem sobrescrever position.
                          position: 'sticky' as const,
                          top: 0,
                          zIndex: header.column.getIsPinned() ? 30 : 20,
                        }
                      : {};

                    return (
                      <TableHead
                        key={header.id}
                        colSpan={header.colSpan}
                        className={cn(stickyHeader && 'sticky top-0 z-20 bg-white')}
                        style={{
                          ...pinningStyles,
                          ...stickyHeaderStyles,
                        }}
                      >
                        <div className={'whitespace-nowrap'}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                        </div>
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody className="text-xs font-sans flex-1 overflow-auto">
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    onClick={() => onRowClick?.(row)}
                    className={cn(
                      onRowClick && 'cursor-pointer',
                      (leftPinnedColumns?.length || rightPinnedColumns?.length) && 'hover:bg-muted/100',
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        style={{ ...getPinningStyles(cell.column) }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center font-sans"
                  >
                    Sem resultados.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-start space-x-2 py-4">
          {selectable && (
            <div className="flex-1 text-sm text-muted-foreground">
              {table.getFilteredSelectedRowModel().rows.length} of{' '}
              {table.getFilteredRowModel().rows.length} linha(s) selecionada(s).
            </div>
          )}
          <DataTablePagination table={table} />
        </div>
      </div>
    );
  });
DataTableV2.displayName = 'DataTableV2';
